import { useId, useState } from 'react';
import { useCreateReceipt } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { Field, Modal, MoneyInput } from '@/components/ui';
import { IconCheck } from '@/components/Icons';
import { formatBRL, LEVERAGE_LONG_LABELS, TRANSFER_LABELS } from '@/lib/format';
import {
  defaultTransferStatus, isAlavancagem, isDeposit, isGas, isTransfer, TRANSFER_RECEIPT_STATUSES, TRANSFER_STATUSES,
} from '@/lib/receipts';
import type { ReceiptType, TransferStatus } from '@/lib/types';
import { ClientAccountFields, type AccountDraft } from '@/components/leads/ClientAccountFields';
import { FilePicker, type PickedFile } from './FilePicker';
import { AUTO_DESCRIPTIONS, emptyDraft, ReceiptFields, validateDraft, type ReceiptDraft } from './ReceiptFields';

interface NewReceiptModalProps {
  onClose: () => void;
  /** Cliente já escolhido (quando o cadastro começa pela tela do cliente). */
  defaultLeadId?: string;
  defaultLeadName?: string;
  /** Membro já escolhido — o administrador anexando pelo cliente de alguém. */
  defaultMemberId?: string;
  /** Tipo já escolhido: "Registrar gás", na tela do cliente, abre como gás; "Nova alavancagem", como boas-vindas. */
  defaultType?: ReceiptType;
  /** Título do modal, quando ele abre por outro caminho ("Nova alavancagem"). */
  title?: string;
}


/** "Anexar comprovante": o tipo primeiro, depois a prova e os dados — o repasse só tem a seção do repasse. */
export function NewReceiptModal({ onClose, defaultLeadId, defaultLeadName, defaultMemberId, defaultType, title }: NewReceiptModalProps) {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const createReceipt = useCreateReceipt();
  const transferGroup = useId();

  const [files, setFiles] = useState<PickedFile[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [draft, setDraft] = useState<ReceiptDraft>(() =>
    emptyDraft({
      leadId: defaultLeadId ?? '',
      leadName: defaultLeadName ?? '',
      memberId: defaultMemberId ?? '',
      ...(defaultType ? { leverageType: defaultType } : {}),
      ...(defaultType && AUTO_DESCRIPTIONS[defaultType] ? { description: AUTO_DESCRIPTIONS[defaultType] } : {}),
    }),
  );
  const [account, setAccount] = useState<AccountDraft>({ accountLogin: '', accountPassword: '' });
  const [transferStatus, setTransferStatus] = useState<TransferStatus>(defaultType ? defaultTransferStatus(defaultType) : 'PENDING');
  const gas = isGas(draft.leverageType);
  const transfer = isTransfer(draft.leverageType);
  const deposit = isDeposit(draft.leverageType);
  // Enquanto a pessoa não escolhe o repasse, ele acompanha o tipo.
  const [transferChosen, setTransferChosen] = useState(false);
  const [transferAmount, setTransferAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const busy = preparing || createReceipt.isPending;

  function changeDraft(patch: Partial<ReceiptDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
    // Outro cliente: o login e a senha digitados eram do anterior.
    if (patch.leadId !== undefined) setAccount({ accountLogin: '', accountPassword: '' });
    if (!patch.leverageType) return;
    // Gás nunca tem repasse; nos outros, o repasse acompanha o tipo até a pessoa escolher.
    if (isGas(patch.leverageType)) {
      setTransferStatus('NONE');
      setTransferAmount(0);
    } else if (isTransfer(patch.leverageType)) {
      // O repasse sempre tem repasse: "sem repasse" vira pendente.
      setTransferStatus((current) => (current === 'NONE' ? 'PENDING' : current));
    } else if (!transferChosen) {
      setTransferStatus(defaultTransferStatus(patch.leverageType));
    }
  }

  function chooseTransfer(status: TransferStatus) {
    setTransferStatus(status);
    setTransferChosen(true);
    if (status !== 'DONE') setTransferAmount(0);
  }

  async function submit() {
    const problem =
      validateDraft(draft, { requireMember: isAdmin, requireType: true, requireLead: true, requireDeposit: true }) ??
      (files.length === 0 ? 'Anexe o comprovante: um print ou um PDF.' : null) ??
      (transfer && transferStatus === 'DONE' && transferAmount <= 0 ? 'Informe quanto foi puxado de repasse.' : null);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);

    const form = new FormData();
    const pulled = !gas && !deposit && transferStatus === 'DONE' ? transferAmount : 0;
    form.set('description', draft.description.trim() || 'Repasse');
    // O repasse não tem valor vendido: guarda o que foi puxado (fora do vendido de todo jeito).
    form.set('soldAmount', String(transfer ? pulled : draft.soldAmount));
    form.set('leverageType', draft.leverageType);
    // O campo antigo "alavancagem colocada" não se usa mais; na alavancagem o depositado vai à parte.
    form.set('leverageAmount', '0');
    if (isAlavancagem(draft.leverageType)) {
      form.set('depositAmount', String(draft.depositAmount));
      form.set('leverageStatus', draft.leverageStatus);
    }
    // Só nos depósitos, onde os campos aparecem: digitados e depois escondidos pela troca de tipo, não vão.
    const withAccount = Boolean(draft.leadId) && (isDeposit(draft.leverageType) || isAlavancagem(draft.leverageType));
    if (withAccount && account.accountLogin.trim()) form.set('accountLogin', account.accountLogin.trim());
    if (withAccount && account.accountPassword) form.set('accountPassword', account.accountPassword);
    form.set('transferStatus', gas || deposit ? 'NONE' : transferStatus);
    form.set('transferAmount', String(pulled));
    form.set('date', draft.date);
    form.set('time', draft.time);
    if (!transfer && draft.note.trim()) form.set('note', draft.note.trim());
    if (draft.leadId) form.set('leadId', draft.leadId);
    if (draft.memberId) form.set('memberId', draft.memberId);
    for (const picked of files) form.append('files', picked.file, picked.file.name);

    try {
      await createReceipt.mutateAsync(form);
      toast.success(
        gas
          ? `Gás de ${formatBRL(draft.soldAmount)} registrado.`
          : transfer
            ? 'Comprovante de repasse anexado.'
            : isAlavancagem(draft.leverageType)
              ? draft.leverageStatus === 'DONE'
                ? `Depósito de ${formatBRL(draft.depositAmount)} com ${formatBRL(draft.soldAmount)} de alavancagem buscada anexado.`
                : `Depósito de ${formatBRL(draft.depositAmount)} anexado. A buscada entra quando a alavancagem for realizada.`
              : `Comprovante de ${(LEVERAGE_LONG_LABELS[draft.leverageType] ?? 'venda').toLowerCase()} de ${formatBRL(draft.soldAmount)} anexado.`,
      );
      onClose();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <Modal
      title={title ?? (gas ? 'Registrar gás' : 'Anexar comprovante')}
      width={660}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={createReceipt.isPending}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? <span className="spinner" /> : <IconCheck />}
            {createReceipt.isPending ? 'Enviando…' : gas ? 'Registrar gás' : 'Anexar comprovante'}
          </button>
        </>
      }
    >
      <ReceiptFields
        draft={draft}
        onChange={changeDraft}
        isAdmin={isAdmin}
        autoFocus
        waitForType
        afterType={
          <FilePicker files={files} onChange={setFiles} onBusyChange={setPreparing} onProblem={(message) => toast.error(message)} />
        }
      />

      {/* Depósito (com ou sem alavancagem): completa a conta do cliente, se faltar — nunca na observação. */}
      {draft.leadId && (isDeposit(draft.leverageType) || isAlavancagem(draft.leverageType)) ? (
        <ClientAccountFields leadId={draft.leadId} value={account} onChange={(patch) => setAccount((current) => ({ ...current, ...patch }))} />
      ) : null}

      {/* O depósito não tem repasse: a seção some, e ele vai como "sem repasse". */}
      {!draft.leverageType || deposit ? null : gas ? (
        <div className="alert alert-info" role="note">
          O gás fica fora do vendido, do ranking, das metas e da comissão. Ele aparece no bloco &ldquo;Gás&rdquo; do painel.
        </div>
      ) : (
        <div className="grid grid2">
          <fieldset className="field" style={{ margin: 0, padding: 0, border: 0 }}>
            <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>{transfer ? 'Repasse *' : 'Repasse'}</legend>
            <div className="choice-group">
              {(transfer ? TRANSFER_RECEIPT_STATUSES : TRANSFER_STATUSES).map((status) => (
                <label key={status} className="choice">
                  <input
                    type="radio"
                    name={transferGroup}
                    value={status}
                    checked={transferStatus === status}
                    onChange={() => chooseTransfer(status)}
                  />
                  {TRANSFER_LABELS[status]}
                </label>
              ))}
            </div>
          </fieldset>
          {transferStatus === 'DONE' ? (
            <Field label={transfer ? 'Repasse puxado (R$) *' : 'Repasse puxado (R$)'} hint="Quanto foi puxado de repasse.">
              <MoneyInput value={transferAmount} onChange={setTransferAmount} />
            </Field>
          ) : null}
        </div>
      )}

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}
    </Modal>
  );
}
